import { signUp } from "aws-amplify/auth";
import axios, { CancelTokenSource } from "axios";
import { paths } from "config/paths";
import { useFormik } from "formik";
import { useWebRouter } from "~utils/next-router/use-web-router";
import { sendSignUpEvent } from "~tag-manager/send-event";
import { useBoolean } from "usehooks-ts";
import { tryCatch } from "~utils/try-catch";
import { useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";

import { normalizeEmail } from "~auth/helpers";
import { isEmailTaken } from "~auth/queries/isEmailTaken";

import { toast } from "~ui-components/components/atoms/toaster";
import {
	SignUpV4FormValues,
	SignUpV4RemoteFormValues,
	SignUpV4RemoteSchema,
	SignUpV4Schema,
	SignUpV4 as View,
} from "~ui-components/components/templates/sign-up-v4";

const stripPreSignUpErrorPrefix = (message: string): string => {
	const prefix = "PreSignUp failed with error ";
	return message.startsWith(prefix)
		? message.slice(prefix.length).trim()
		: message;
};

let emailValidationSource: CancelTokenSource | null = null;

export const SignUpV4View = () => {
	const router = useWebRouter();
	const recaptchaRef = useRef<ReCAPTCHA>(null);
	const form = useFormik<SignUpV4FormValues>({
		validateOnMount: true,
		initialValues: {
			name: "",
			password: "",
			acceptedToTermsAndPolicy: false,
			companyName: "",
		},
		validationSchema: SignUpV4Schema,
		validate: (values) => {
			const [firstName, lastName] = values.name.split(" ");
			if (!firstName || !lastName)
				return {
					name: "Please enter your first and last name",
				};

			return {};
		},
		onSubmit: (values, helpers) => {
			const payload = {
				...values,
				...remoteForm.values,
				email: normalizeEmail(remoteForm.values.email),
			};

			const resetRecaptcha = () => {
				recaptchaRef.current?.reset();
			};

			toast.promise(
				async () => {
					const recaptchaToken = await recaptchaRef.current?.executeAsync();
					if (!recaptchaToken) {
						resetRecaptcha();
						throw new Error("Please complete the verification and try again.");
					}

					try {
						const { nextStep } = await signUp({
							username: payload.email,
							password: payload.password,
							options: {
								autoSignIn: true,
								userAttributes: {
									"custom:signup:email": payload.email,
									"custom:signup:name": payload.name,
									"custom:signup:companyName": payload.companyName,
								},
								validationData: {
									recaptchaToken,
								},
							},
						});
						if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
							sendSignUpEvent();

							void router.replace(paths.signUpVerificationV4(payload.email));
							return;
						}

						throw new Error("Something went wrong!");
					} catch (e) {
						resetRecaptcha();
						throw e;
					}
				},
				{
					loading: "Signing you up...",
					success: "Success!",
					error: (e) => {
						helpers.setSubmitting(false);
						if (axios.isAxiosError(e)) return "Something went wrong!";
						return stripPreSignUpErrorPrefix(
							e instanceof Error ? e.message : "Something went wrong!",
						);
					},
				},
			);
		},
	});
	const remoteForm = useFormik<SignUpV4RemoteFormValues>({
		validateOnMount: true,
		validateOnBlur: false,
		validateOnChange: false,
		initialValues: {
			email: "",
		},
		validationSchema: SignUpV4RemoteSchema,
		// eslint-disable-next-line @typescript-eslint/no-empty-function -- only use this form for validation
		onSubmit: () => undefined,
	});
	const { value: isRemotelyValidatingEmail, setValue: toggleValidatingEmail } =
		useBoolean(false);

	const handleEmailValueChange = async (newValue: string) => {
		const [schemaErrorMessage] = await Promise.all([
			SignUpV4RemoteSchema.fields.email
				.validate(newValue)
				.then(() => null)
				.catch((e) => e.message as string),
			remoteForm.setFieldValue("email", newValue),
			remoteForm.setFieldTouched("email", true),
		]);
		if (schemaErrorMessage) {
			remoteForm.setFieldError("email", schemaErrorMessage);
			return;
		}

		toggleValidatingEmail(true);
		const [remoteError] = await tryCatch(remotelyValidateEmail(newValue));
		if (remoteError) {
			if (axios.isCancel(remoteError)) return;

			toggleValidatingEmail(false);
			remoteForm.setFieldError("email", remoteError.message);
			return;
		}
		toggleValidatingEmail(false);
		remoteForm.setFieldError("email", undefined);
	};

	return (
		<>
			<View
				form={form}
				remoteForm={remoteForm}
				onEmailChange={(e) => handleEmailValueChange(e.target.value)}
				onEmailBlur={(e) => handleEmailValueChange(e.target.value)}
				isRemotelyValidatingEmail={isRemotelyValidatingEmail}
			/>
			<ReCAPTCHA
				ref={recaptchaRef}
				size="invisible"
				sitekey={process.env.GOOGLE_RECAPTCHA_SITE_KEY ?? ""}
			/>
		</>
	);
};

const remotelyValidateEmail = async (email: string) => {
	const normalizedEmail = normalizeEmail(email);
	if (emailValidationSource) emailValidationSource.cancel();
	emailValidationSource = axios.CancelToken.source();

	const [error, bool] = await tryCatch(
		isEmailTaken(
			{ email: normalizedEmail },
			{ cancelToken: emailValidationSource.token },
		),
	);

	if (error) {
		if (axios.isCancel(error)) throw error;
		if (axios.isAxiosError(error)) throw new Error("Something went wrong!");
		throw new Error(error.message);
	}

	if (bool) throw new Error("Email is already taken");
};
